import dayjs, { extend, type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useMyInfoStore } from '@/config/stores/my-info';

extend(utc);
extend(timezone);

const normalizeUtcZeroTime = (utcZeroTime: string): string => {
  const utcZeroTimeLength = utcZeroTime.length;

  if (utcZeroTimeLength - 1 === utcZeroTime.lastIndexOf('Z')) {
    return utcZeroTime.slice(0, utcZeroTimeLength - 1);
  }

  if (utcZeroTimeLength - 4 === utcZeroTime.lastIndexOf(' GMT')) {
    return utcZeroTime.slice(0, utcZeroTimeLength - 4);
  }

  return utcZeroTime;
};

// wall clock 을 문자열로 정규화할 때 쓰는 포맷.
// 밀리초까지 포함해야 `returnFormat` 이 `.SSS` 를 요구하는 호출부에서 값이 잘리지 않는다.
const NORMALIZE_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';

/**
 * wall clock(연월일시분초)을 유지한 채 대상 timezone 으로 재해석
 *
 * `Dayjs.tz(tz, true)` 는 원본 instant 시점의 offset 으로 보정해, 그 instant 와 목표 wall clock
 * 이 DST 경계를 사이에 두면 한 시간 어긋난다. 반환값은 instant 계열(`.utc()` / `.unix()` /
 * `.valueOf()`)만 신뢰할 수 있고 `.format()` 은 브라우저 tz 의 전환일에 어긋나므로, 표시
 * 문자열은 UTC 를 거쳐 만든다. invalid 입력은 `'Invalid Date'` / `NaN` sentinel 계약 때문에
 * throw 하지 않고 그대로 통과시킨다.
 */
export const toTimezoneKeepingWallClock = (source: Dayjs, targetTimezone: string): Dayjs =>
  source.isValid()
    ? dayjs.tz(source.format(NORMALIZE_FORMAT), NORMALIZE_FORMAT, targetTimezone)
    : source;

/**
 * 표준 시 -> UTC 0로 변환
 */
export const standardTimeToUtcZeroTime = (
  standardTime?: number | string,
  returnFormat = 'YYYY-MM-DD HH:mm:ss',
): string => {
  if (!standardTime) {
    return standardTime as any;
  }
  const { timezone } = useMyInfoStore();
  return toTimezoneKeepingWallClock(dayjs(standardTime), timezone).utc().format(returnFormat);
};

/**
 * UTC 0 -> 표준 시로 변환
 */
export const utcZeroTimeToStandardTime = (
  utcZeroTime: undefined | null | string,
  returnFormat = 'YYYY-MM-DD HH:mm:ss',
): string => {
  if (!utcZeroTime) {
    return '';
  }

  const { timezone: tz } = useMyInfoStore();
  return dayjs.utc(normalizeUtcZeroTime(utcZeroTime)).tz(tz).format(returnFormat);
};

const UTC_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/**
 * UTC datetime 포맷(`YYYY-MM-DDTHH:mm:ss`)이면 표준시로 변환, 아니면 그대로 반환
 */
export const convertUtcStringToStandardTime = (value: string): string =>
  UTC_DATETIME_REGEX.test(value) ? utcZeroTimeToStandardTime(value) : value;
